import {
  Timestamp,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

export interface DuplicateDetectionConfig {
  enabled: boolean;
  windowMinutes: number;
  scoreThreshold: number;
  distanceMeters: number;
  ttsEnabled: boolean;
  updatedAt?: Date;
  storeId?: string | null;
}

type DuplicateDetectionOptions = {
  storeId?: string | null;
};

const DEFAULT_CONFIG: DuplicateDetectionConfig = {
  enabled: true,
  windowMinutes: 15,
  scoreThreshold: 0.7,
  distanceMeters: 120,
  ttsEnabled: true,
};

const scopedRef = (storeId: string) => doc(db, 'duplicate_detection', storeId);

const toJsDate = (value: unknown): Date | undefined => {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return undefined;
};

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const sanitizeConfig = (
  partial: Partial<DuplicateDetectionConfig>
): Partial<DuplicateDetectionConfig> => {
  const next: Partial<DuplicateDetectionConfig> = {};

  if (partial.enabled !== undefined) {
    next.enabled = !!partial.enabled;
  }
  if (partial.windowMinutes !== undefined) {
    next.windowMinutes = Math.round(clampNumber(Number(partial.windowMinutes), 2, 60));
  }
  if (partial.scoreThreshold !== undefined) {
    next.scoreThreshold = clampNumber(Number(partial.scoreThreshold), 0.6, 0.9);
  }
  if (partial.distanceMeters !== undefined) {
    next.distanceMeters = clampNumber(Number(partial.distanceMeters), 50, 500);
  }
  if (partial.ttsEnabled !== undefined) {
    next.ttsEnabled = !!partial.ttsEnabled;
  }

  return next;
};

const buildConfig = (
  data: Record<string, any> | undefined,
  storeId?: string | null
): DuplicateDetectionConfig => {
  if (!data) {
    return { ...DEFAULT_CONFIG, storeId: storeId || null };
  }

  return {
    enabled: typeof data.enabled === 'boolean' ? data.enabled : DEFAULT_CONFIG.enabled,
    windowMinutes:
      typeof data.windowMinutes === 'number'
        ? Math.round(clampNumber(data.windowMinutes, 2, 60))
        : DEFAULT_CONFIG.windowMinutes,
    scoreThreshold:
      typeof data.scoreThreshold === 'number'
        ? clampNumber(data.scoreThreshold, 0.6, 0.9)
        : DEFAULT_CONFIG.scoreThreshold,
    distanceMeters:
      typeof data.distanceMeters === 'number'
        ? clampNumber(data.distanceMeters, 50, 500)
        : DEFAULT_CONFIG.distanceMeters,
    ttsEnabled: typeof data.ttsEnabled === 'boolean' ? data.ttsEnabled : DEFAULT_CONFIG.ttsEnabled,
    updatedAt: toJsDate(data.updatedAt || data.lastUpdated),
    storeId: storeId || (typeof data.storeId === 'string' ? data.storeId : null),
  };
};

export async function getDuplicateDetectionConfig(
  storeId?: string | null
): Promise<DuplicateDetectionConfig> {
  if (storeId) {
    const scopedSnap = await getDoc(scopedRef(storeId));
    if (scopedSnap.exists()) {
      return buildConfig(scopedSnap.data() as Record<string, any>, storeId);
    }
  }

  return { ...DEFAULT_CONFIG, storeId: storeId || null };
}

export async function updateDuplicateDetectionConfig(
  partial: Partial<DuplicateDetectionConfig>,
  options: DuplicateDetectionOptions = {}
): Promise<DuplicateDetectionConfig> {
  const storeId = options.storeId || null;
  const sanitized = sanitizeConfig(partial);

  if (!storeId) {
    throw new Error('Aucun magasin actif sélectionné pour enregistrer la détection des doublons.');
  }

  const batch = writeBatch(db);
  const primaryRef = scopedRef(storeId);

  batch.set(
    primaryRef,
    {
      ...sanitized,
      storeId,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await batch.commit();
  return await getDuplicateDetectionConfig(storeId);
}
